import type { AudiencePreviewRow, CampaignChannelKind } from '@workspace/types';
import { formatCampaignMoney } from './ContactAgentMetricCard.utils';
import { selectedPreviewBalance, toggleIncludedId } from '../api/campaignAudienceInclusion';
import { reachabilityMissingFieldLabel } from '../api/campaignDisplay';

export function CampaignAudiencePreviewLists({
  channelKind,
  reachable,
  missingUnreachable,
  reachableTotal,
  missingUnreachableTotal,
  reachableAmount,
  missingUnreachableAmount,
  includedIds,
  onIncludedIds,
}: {
  channelKind: CampaignChannelKind;
  reachable: AudiencePreviewRow[];
  missingUnreachable: AudiencePreviewRow[];
  reachableTotal?: number;
  missingUnreachableTotal?: number;
  reachableAmount?: number;
  missingUnreachableAmount?: number;
  includedIds?: string[];
  onIncludedIds?: (ids: string[]) => void;
}) {
  const channelLabel = reachabilityMissingFieldLabel(channelKind);
  const selectable = onIncludedIds !== undefined && includedIds !== undefined;
  const selectedCount = selectable
    ? reachable.filter(row => row.contactId && includedIds.includes(row.contactId)).length
    : reachableTotal ?? reachable.length;
  const selectedAmount = selectedPreviewBalance(
    reachable,
    selectable ? includedIds : undefined,
    reachableAmount,
  );
  return (
    <div className='grid gap-4 lg:grid-cols-2'>
      <PreviewColumn
        count={selectedCount}
        amount={selectedAmount}
        title={`Entran (tienen ${channelLabel})`}
        empty={`Nadie entra con estos montos y cantidades.`}
        rows={reachable}
        channelKind={channelKind}
        tone='ok'
        includedIds={selectable ? includedIds : undefined}
        onIncludedIds={selectable ? onIncludedIds : undefined}
      />
      <PreviewColumn
        count={missingUnreachableTotal ?? missingUnreachable.length}
        amount={missingUnreachableAmount ?? selectedPreviewBalance(missingUnreachable, undefined)}
        title={`Quedan afuera por falta de ${channelLabel}`}
        empty={`Nadie con estos filtros queda afuera por ${channelLabel}.`}
        rows={missingUnreachable}
        channelKind={channelKind}
        tone='warn'
      />
    </div>
  );
}

function PreviewColumn({
  count,
  amount,
  title,
  empty,
  rows,
  channelKind,
  tone,
  includedIds,
  onIncludedIds,
}: {
  count: number;
  amount: number;
  title: string;
  empty: string;
  rows: AudiencePreviewRow[];
  channelKind: CampaignChannelKind;
  tone: 'ok' | 'warn';
  includedIds?: string[];
  onIncludedIds?: (ids: string[]) => void;
}) {
  const selectable = onIncludedIds !== undefined && includedIds !== undefined;
  const box =
    tone === 'warn'
      ? 'rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
      : 'rounded-xl border bg-card';
  const selectableIds = rows.flatMap(row => (row.contactId ? [row.contactId] : []));
  const allSelected = selectable && selectableIds.length > 0 && selectableIds.every(id => includedIds?.includes(id));
  return (
    <div className={`${box} p-4 text-sm`}>
      <p className='text-3xl font-semibold tabular-nums'>{count}</p>
      <p className='text-lg font-semibold tabular-nums'>{formatCampaignMoney(String(amount))}</p>
      <p className='font-medium'>{title}</p>
      {selectable ? (
        <div className='mt-2 flex flex-wrap items-center gap-2'>
          <p className='text-xs text-muted-foreground'>Desmarcá a quien no quieras incluir.</p>
          <button
            type='button'
            className='text-xs underline'
            onClick={() => onIncludedIds?.(allSelected ? [] : selectableIds)}
          >
            {allSelected ? 'Quitar todos' : 'Marcar todos'}
          </button>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className='mt-2 text-muted-foreground'>{empty}</p>
      ) : (
        <ul className='mt-2 max-h-80 overflow-auto divide-y'>
          {rows.map((row, index) => (
            <PreviewRowItem
              key={`${row.contactId ?? row.externalRef ?? row.name}-${index}`}
              row={row}
              channelKind={channelKind}
              includedIds={includedIds}
              onIncludedIds={onIncludedIds}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PreviewRowItem({
  row,
  channelKind,
  includedIds,
  onIncludedIds,
}: {
  row: AudiencePreviewRow;
  channelKind: CampaignChannelKind;
  includedIds?: string[];
  onIncludedIds?: (ids: string[]) => void;
}) {
  const contactId = row.contactId;
  const selectable = onIncludedIds !== undefined && includedIds !== undefined && Boolean(contactId);
  const contactLine =
    channelKind === 'EMAIL'
      ? row.email
      : row.phoneE164;
  const body = (
    <>
      <p>
        {row.name}
        {contactLine ? (
          <span className='ml-2 font-normal text-muted-foreground tabular-nums'>{contactLine}</span>
        ) : null}
      </p>
      <p className='text-xs text-muted-foreground'>
        {formatCampaignMoney(String(row.totalBalance))} · {row.invoiceCount} factura
        {row.invoiceCount === 1 ? '' : 's'}
        {row.externalRef ? ` · código ${row.externalRef}` : ''}
      </p>
    </>
  );
  if (!selectable || !contactId) {
    return <li className='py-2'>{body}</li>;
  }
  return (
    <li className='py-2'>
      <label className='flex cursor-pointer items-start gap-3'>
        <input
          type='checkbox'
          className='mt-1'
          checked={includedIds.includes(contactId)}
          onChange={() => onIncludedIds(toggleIncludedId(includedIds, contactId))}
        />
        <span>{body}</span>
      </label>
    </li>
  );
}
